import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Crosshair, Flame, Trophy, Target, Lock, Gamepad2, Eye, BookOpen, ArrowRight } from "lucide-react";
import Leaderboard from "@/components/Leaderboard";
import SkillPreview from "@/components/SkillPreview";
import WeeklyGoalCard from "@/components/WeeklyGoalCard";

const SKILL_NAMES = [
  "Addition Facts to 18",
  "Subtraction Facts from 18",
  "Multiplication Facts to 144",
  "Division Facts from 144",
];

const SHIP_SHAPES = {
  0: "M12 2 L22 22 L12 17 L2 22 Z",
  1: "M12 0 L18 10 L24 22 L12 17 L0 22 L6 10 Z",
  2: "M12 0 L19 8 L26 22 L12 16 L-2 22 L5 8 Z",
  3: "M12 0 L17 5 L24 10 L26 20 L18 24 L12 22 L6 24 L-2 20 L0 10 L7 5 Z",
};

export default function StudentDashboard() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [ships, setShips] = useState([]);
  const [progress, setProgress] = useState(null);
  const [mastery, setMastery] = useState(null);
  const [classCode, setClassCode] = useState("");
  const [joiningClass, setJoiningClass] = useState(false);
  const [joinError, setJoinError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, shipsRes, progRes, masteryRes] = await Promise.all([
          API.get("/student/stats"),
          API.get("/student/ships"),
          API.get("/practice/progress"),
          API.get("/practice/mastery"),
        ]);
        setMastery(masteryRes.data);
        setStats(statsRes.data);
        setShips(shipsRes.data.ships);
        setProgress(progRes.data);
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, []);

  const hasActiveSession = progress?.active_session;
  const rewardReady = progress?.reward_earned && !progress?.reward_used;
  const hasClass = !!user?.class_id;
  const bandIdx = Math.min(user?.current_skill_band || 0, SKILL_NAMES.length - 1);
  const bandPct = mastery?.total_facts ? Math.round((mastery.mastered_count / mastery.total_facts) * 100) : 0;

  const handleJoinClass = async (e) => {
    e?.preventDefault();
    if (!classCode.trim()) return;
    setJoiningClass(true);
    setJoinError("");
    try {
      const { data } = await API.post("/student/join-class", { class_code: classCode.trim() });
      await refreshUser();
      setStats((prev) => prev ? { ...prev, class_info: { name: data.class_name, id: data.class_id, code: classCode.trim() } } : prev);
      setClassCode("");
    } catch (err) {
      setJoinError(err.response?.data?.detail || "Invalid class code");
    } finally {
      setJoiningClass(false);
    }
  };

  return (
    <div className="min-h-screen p-6 lg:p-10 max-w-6xl mx-auto" data-testid="student-dashboard">
      {/* Join Class - shown when student has no class */}
      {!hasClass && (
        <div className="mb-8 p-6 rounded-xl border-2 border-dashed border-cyber/30 bg-cyber/5" data-testid="join-class-section">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-5 h-5 text-cyber" />
            <h2 className="font-heading text-lg font-bold text-white">Join Your Class</h2>
          </div>
          <p className="font-body text-sm text-white/50 mb-4">
            Enter the class code from your teacher to join their class and appear on the leaderboard.
          </p>
          <form onSubmit={handleJoinClass} className="flex items-center gap-3">
            <Input
              value={classCode}
              onChange={(e) => { setClassCode(e.target.value.toUpperCase()); setJoinError(""); }}
              placeholder="e.g. AB3K-7X2M"
              data-testid="join-class-code-input"
              className="flex-1 max-w-xs bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-cyber focus:ring-cyber/30 font-mono text-lg tracking-wider h-12"
            />
            <button
              type="submit"
              disabled={joiningClass || !classCode.trim()}
              data-testid="join-class-btn"
              className="btn-cyber h-12 px-6 rounded-lg font-heading text-sm tracking-wider flex items-center gap-2 disabled:opacity-40"
            >
              {joiningClass ? "JOINING..." : "JOIN"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
          {joinError && (
            <p className="font-body text-sm text-magenta mt-2" data-testid="join-class-error">{joinError}</p>
          )}
        </div>
      )}

      {/* Class info if enrolled */}
      {hasClass && stats?.class_info && (
        <div className="mb-4 flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-cyber/50" />
          <span className="font-body text-sm text-white/40">
            Class: <span className="text-white/60">{stats.class_info.name}</span>
          </span>
        </div>
      )}

      {/* Header */}
      <div className="mb-10">
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white mb-2">
          Welcome, <span className="neon-text-cyber">{user?.name}</span>
        </h1>
        <p className="font-body text-white/50 text-base">
          Skill Band {bandIdx + 1}: {SKILL_NAMES[bandIdx]}
        </p>
      </div>

      <WeeklyGoalCard />

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard icon={Target} label="Accuracy" value={`${stats?.accuracy || 0}%`} color="cyber" />
        <StatCard icon={Flame} label="Best Score" value={stats?.best_score || 0} color="magenta" />
        <StatCard icon={Trophy} label="Games Played" value={stats?.games_played || 0} color="neon" />
        <StatCard icon={Crosshair} label="Problems Solved" value={stats?.total_attempts || 0} color="cyber" />
      </div>

      {/* Active Session / CTA */}
      {rewardReady ? (
        <button
          onClick={() => navigate("/arcade")}
          data-testid="launch-arcade-btn"
          className="w-full mb-10 p-6 rounded-xl border border-cyber/30 bg-cyber/5 hover:bg-cyber/10 transition-colors duration-200 group animate-glow-pulse"
        >
          <div className="flex items-center justify-center gap-4">
            <Gamepad2 className="w-8 h-8 text-cyber group-hover:scale-110 transition-transform duration-200" />
            <span className="font-heading text-2xl font-bold text-cyber tracking-tight">
              REWARD UNLOCKED — LAUNCH ARCADE
            </span>
          </div>
          <p className="text-white/40 font-body text-sm mt-2">
            {progress?.bonus_mode ? "BONUS MODE: Play until death!" : "Standard mode: 60 second session"}
          </p>
        </button>
      ) : (
        <button
          onClick={() => navigate("/practice")}
          data-testid="start-practice-btn"
          className="w-full mb-10 p-6 rounded-xl btn-cyber text-left group"
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="font-heading text-xl font-bold tracking-tight block">
                {hasActiveSession ? "CONTINUE PRACTICE" : "START PRACTICE"}
              </span>
              {hasActiveSession && (
                <span className="font-mono text-sm opacity-70 mt-1 block">
                  {progress?.session_correct}/20 correct | Streak: {progress?.session_streak}
                </span>
              )}
            </div>
            <Crosshair className="w-8 h-8 opacity-60 group-hover:opacity-100 group-hover:scale-110 transition-all duration-200" />
          </div>
        </button>
      )}

      {/* Ships */}
      <div className="mb-10">
        <h2 className="font-heading text-lg font-bold text-white mb-4 tracking-tight">
          Your Fleet
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {ships.map((ship) => (
            <div
              key={ship.id}
              data-testid={`ship-card-${ship.id}`}
              className={`relative p-5 rounded-xl border transition-all duration-200 ${
                ship.unlocked
                  ? `border-white/10 hover:border-[${ship.color}]/50 hover:-translate-y-1`
                  : "border-white/5 opacity-40"
              }`}
              style={ship.unlocked ? { borderColor: `${ship.color}20` } : {}}
            >
              {!ship.unlocked && (
                <div className="absolute top-3 right-3">
                  <Lock className="w-4 h-4 text-white/30" />
                </div>
              )}
              <div className="flex justify-center mb-3">
                <svg width="48" height="48" viewBox="0 0 26 26">
                  <path
                    d={SHIP_SHAPES[ship.id]}
                    fill={ship.unlocked ? ship.color : "#333"}
                    opacity={ship.unlocked ? 1 : 0.3}
                    style={ship.unlocked ? { filter: `drop-shadow(0 0 6px ${ship.color})` } : {}}
                  />
                </svg>
              </div>
              <p className="font-heading text-sm font-bold text-white text-center">{ship.name}</p>
              <p className="font-body text-xs text-white/40 text-center mt-1">{ship.desc}</p>
              {!ship.unlocked && (
                <p className="font-mono text-xs text-white/20 text-center mt-2">
                  Band {ship.min_band + 1}+
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Leaderboard + Level Preview */}
      <div className="grid lg:grid-cols-2 gap-6 mb-10">
        <Leaderboard />
        <div className="glass-surface rounded-xl p-6" data-testid="next-level-preview">
          <div className="flex items-center gap-2 mb-4">
            <Eye className="w-5 h-5 text-neon" />
            <h2 className="font-heading text-lg font-bold text-white">Current Level Preview</h2>
          </div>
          <SkillPreview
            bandId={user?.current_skill_band || 0}
            onReady={() => navigate("/practice")}
          />
        </div>
      </div>

      {/* Recent Games */}
      {stats?.recent_games?.length > 0 && (
        <div>
          <h2 className="font-heading text-lg font-bold text-white mb-4 tracking-tight">
            Recent Games
          </h2>
          <div className="glass-surface rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left p-4 font-mono text-xs text-white/40 uppercase tracking-wider">Mode</th>
                  <th className="text-left p-4 font-mono text-xs text-white/40 uppercase tracking-wider">Score</th>
                  <th className="text-left p-4 font-mono text-xs text-white/40 uppercase tracking-wider">Duration</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_games.map((game, i) => (
                  <tr key={game._id || i} className="border-b border-white/5 last:border-0">
                    <td className="p-4">
                      <span className={`font-mono text-sm ${game.mode === "bonus" ? "text-cyber" : "text-white/70"}`}>
                        {game.game === "invaders" ? "INVADERS" : "ASTEROIDS"} · {game.mode?.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-4 font-mono text-sm text-white">{game.score}</td>
                    <td className="p-4 font-mono text-sm text-white/50">{Math.round(game.duration)}s</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Skill Progress */}
      <div className="mt-10">
        <h2 className="font-heading text-lg font-bold text-white mb-4 tracking-tight">
          Skill Progress
        </h2>
        <div className="glass-surface rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="font-body text-sm text-white/50" data-testid="band-label">
              Band {bandIdx + 1} of {SKILL_NAMES.length}: {SKILL_NAMES[bandIdx]}
            </span>
            <span className="font-mono text-xs text-cyber" data-testid="band-percent">
              {bandPct}% mastered ({mastery?.mastered_count || 0}/{mastery?.total_facts || 0} facts)
            </span>
          </div>
          <Progress
            value={bandPct}
            className="h-2 bg-white/5"
            data-testid="skill-progress-bar"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            {SKILL_NAMES.map((name, i) => (
              <span
                key={i}
                className={`text-xs px-2 py-1 rounded font-body ${
                  i < bandIdx
                    ? "bg-cyber/10 text-cyber"
                    : i === bandIdx
                    ? "bg-magenta/10 text-magenta border border-magenta/20"
                    : "bg-white/5 text-white/20"
                }`}
              >
                {i + 1}. {name}
              </span>
            ))}
          </div>
        </div>
      </div>
      {/* Teacher access restore (small, out of students' way) */}
      {user?.role === "student" && (
        <div className="mt-10 text-right" data-testid="admin-role-fix-banner">
          <button
            onClick={async () => {
              try {
                const pw = prompt("Enter the admin password to get teacher access:");
                if (!pw) return;
                const { data } = await API.post("/auth/claim-teacher", { email: user.email, password: pw });
                if (data.access_token) localStorage.setItem("access_token", data.access_token);
                window.location.href = "/teacher";
              } catch { alert("Invalid password or an error occurred."); }
            }}
            data-testid="restore-teacher-btn"
            className="font-body text-xs text-white/20 hover:text-white/50 underline transition-colors duration-200"
          >
            Teacher? Restore access
          </button>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colorMap = { cyber: "text-cyber", magenta: "text-magenta", neon: "text-neon" };
  return (
    <div className="glass-surface rounded-xl p-5 hover:-translate-y-1 transition-transform duration-200">
      <Icon className={`w-5 h-5 ${colorMap[color]} mb-3`} />
      <p className="font-mono text-2xl font-bold text-white" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
        {value}
      </p>
      <p className="font-body text-xs text-white/40 mt-1">{label}</p>
    </div>
  );
}
